import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SquareArrowOutUpRight } from 'lucide-react';
import type { BoardTask } from '../../lib/boardGrouping';

const PRIORITY_COLOR: Record<string, string> = {
  urgent: '#ef4444', high: '#f59e0b', medium: '#a0a6be', low: '#636b88',
};

const CARD_CLASS = 'relative group bg-bg-card border border-border rounded-lg p-2.5 flex flex-col gap-2';

/** The card's visual content, shared by the sortable card and the drag overlay. */
function CardContent({ task, scopeColors }: {
  task: BoardTask; scopeColors: Map<string, { name: string; color: string }>;
}) {
  const scopeTags = task.scope_ids.map(id => scopeColors.get(id)).filter(Boolean) as { name: string; color: string }[];
  const hasMeta = scopeTags.length > 0 || !!task.assignee_name;
  return (
    <>
      <div className="flex items-start gap-2">
        <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: PRIORITY_COLOR[task.priority] }} />
        <span className={`text-sm leading-snug pr-5 ${task.status === 'done' ? 'line-through text-text-muted' : 'text-text-primary'}`}>
          {task.title}
        </span>
      </div>
      {/* Only render the meta row when it has content — an empty row leaves a visible gap below the title (see #205 review) */}
      {hasMeta && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {scopeTags.map(s => (
            <span key={s.name} className="text-[11px] px-1.5 py-0.5 rounded-full"
              style={{ background: s.color + '22', color: s.color }}>{s.name}</span>
          ))}
          {task.assignee_name && <span className="text-[11px] text-text-secondary">@{task.assignee_name}</span>}
        </div>
      )}
    </>
  );
}

export function BoardCard({ task, fromCol, scopeColors, onOpenTask }: {
  task: BoardTask; fromCol: string; scopeColors: Map<string, { name: string; color: string }>;
  onOpenTask: (taskId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id, data: { fromCol },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    // The card body is a pure drag handle — it deliberately has NO onClick. Opening the task
    // is ONLY via the explicit top-right icon, so a click/press meant as a drag never opens the modal (#210).
    // While dragging, the in-place card becomes a dashed outline showing where it will land.
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className={`${CARD_CLASS} cursor-grab ${isDragging ? 'border-dashed !border-accent bg-accent/10' : 'hover:border-border-hover'}`}>
      <button
        type="button"
        aria-label="Open task"
        onPointerDown={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); onOpenTask(task.id); }}
        className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded-md bg-bg-input border border-border text-text-secondary hover:text-text-primary transition-opacity">
        <SquareArrowOutUpRight size={13} />
      </button>
      <CardContent task={task} scopeColors={scopeColors} />
    </div>
  );
}

/** Presentational clone rendered inside the DragOverlay so the dragged card follows the cursor smoothly. */
export function BoardCardOverlay({ task, scopeColors }: {
  task: BoardTask; scopeColors: Map<string, { name: string; color: string }>;
}) {
  return (
    <div className={`${CARD_CLASS} cursor-grabbing border-border-hover shadow-2xl shadow-black/50`}>
      <CardContent task={task} scopeColors={scopeColors} />
    </div>
  );
}
